"use client";

import type { StepDisplay } from "@/generated/sb";
import { StepDisplay as StepDisplayUI } from "@repo/ui-parkmobile/StepDisplay";
import {
  buttonResolver,
  titleResolver,
  textResolver,
  imageStoryblokResolver,
} from "@/lib/storyblok/adaptors";
import { storyblokEditable } from "@storyblok/react/rsc";
import {
  getSectionNameFromTypography,
  getContentDescriptionFromTypography,
} from "@/utils/analytics";
import { trackCtaClick, trackParkingFlow } from "@/utils/trackingScripts";

interface StepDisplayProps {
  blok: StepDisplay;
}

export default function StepDisplay({ blok }: StepDisplayProps) {
  if (!blok) {
    return null;
  }

  const sectionName = getSectionNameFromTypography(blok.typography);
  const contentDescription = getContentDescriptionFromTypography(
    blok.typography,
  );

  const getStepType = (
    stepIndex: number,
  ): "enterZone" | "setTime" | "selectVehicle" | "payAndGo" | null => {
    switch (stepIndex) {
      case 0:
        return "enterZone";
      case 1:
        return "setTime";
      case 2:
        return "selectVehicle";
      case 3:
        return "payAndGo";
      default:
        return null;
    }
  };

  const handleStepInteraction = (stepIndex: number, stepTitle: string) => {
    const stepType = getStepType(stepIndex);
    if (!stepType) return;

    trackParkingFlow({
      step: stepType,
      section: sectionName || "How It Works",
      config: "stepDisplay",
    });
  };

  const typography = blok.typography
    ?.map((item) => {
      switch (item.component) {
        case "title": {
          const titleProps = titleResolver([item]);
          return titleProps
            ? {
                type: "title" as const,
                props: { ...titleProps, ...storyblokEditable(item) },
              }
            : null;
        }
        case "simpleRichText": {
          const textProps = textResolver([item]);
          return textProps
            ? {
                type: "text" as const,
                props: { ...textProps, ...storyblokEditable(item) },
              }
            : null;
        }
        case "list": {
          const listItems = item.items?.map((listItem) => {
            const featureIconImage = listItem.icon?.[0];
            const featureIconAsset = featureIconImage
              ? imageStoryblokResolver(featureIconImage)
              : undefined;
            return {
              icon: featureIconAsset,
              text: listItem.text,
            };
          });
          return {
            type: "list" as const,
            props: { items: listItems || [], ...storyblokEditable(item) },
          };
        }
        default:
          return null;
      }
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const buttons = blok.buttons
    ?.map((buttonBlok) => {
      const button = buttonResolver(buttonBlok);
      if (!button) return null;

      const analyticsEvent = buttonBlok.analyticsEvent || "ctaClick";

      if (analyticsEvent === "ctaClick") {
        const originalOnClick = button.onClick;
        return {
          ...button,
          onClick: () => {
            trackCtaClick({
              event: "ctaClick",
              section: sectionName,
              ctaText: button.children?.toString() || "",
              config: "button",
              contentDescription: contentDescription,
              link: button.href || button.navatticUrl || "",
            });

            originalOnClick?.();
          },
        };
      }

      return button;
    })
    .filter((button): button is NonNullable<typeof button> => button !== null);

  const steps =
    blok.steps?.map((step) => {
      const numberIconImage = step.numberIcon?.[0];
      const numberIconAsset = imageStoryblokResolver(numberIconImage);

      const stepImage = step.image?.[0];
      const stepImageAsset = imageStoryblokResolver(stepImage);

      const titleText = step.title?.[0]?.content || "";

      return {
        numberIcon: numberIconAsset,
        title: titleText,
        image: stepImageAsset,
        ...storyblokEditable(step),
      };
    }) || [];

  return (
    <div {...storyblokEditable(blok)}>
      <StepDisplayUI
        typography={typography}
        buttons={buttons}
        steps={steps}
        backgroundColor={blok.backgroundColor || "none"}
        topPadding={blok.topPadding || "none"}
        bottomPadding={blok.bottomPadding || "none"}
        id={blok.anchor}
        onStepInteraction={handleStepInteraction}
      />
    </div>
  );
}
